import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material'
import type { APIAgentPrompt } from '@visorpro/client/dist/types/APIAgentPrompt'
import { observer } from 'mobx-react-lite'
import type { FC } from 'react'
import { useState } from 'react'
import type { SearchableSelectOption } from '../../components/searchable-select'
import { SearchableSelect } from '../../components/searchable-select'
import { useOrganizations } from '../../stores/organization-store'
import type { Stores } from '../../util/store'

export interface AssignOrganizationsModalProps {
    prompt: APIAgentPrompt
    stores: Stores
    onClose(): void
    onSave(organizationIds: string[]): void
}

export const AssignOrganizationsModal: FC<AssignOrganizationsModalProps> =
    observer(({ prompt, stores, onClose, onSave }) => {
        const organizations = useOrganizations(stores.organizations)

        const [selected, setSelected] = useState<SearchableSelectOption[]>(() =>
            prompt.organizations.map(o => ({ label: o.name, value: o.id })),
        )

        const options = organizations.map(organization => ({
            label: organization.name,
            value: organization.id,
        }))

        return (
            <Dialog
                open
                fullWidth
                maxWidth="sm"
                data-testid="assign-organizations-modal"
                onClose={onClose}>
                <DialogTitle>Organizations for “{prompt.name}”</DialogTitle>
                <DialogContent>
                    <p
                        className="color-dimmed"
                        style={{ fontSize: '0.8rem', marginBottom: '1rem' }}>
                        An organization gets exactly one Vi 3.0 prompt. Adding
                        one here moves it off whatever prompt it had before.
                        Organizations left off take the promoted default.
                    </p>
                    <SearchableSelect
                        multiple
                        autoFocus
                        value={selected}
                        options={options}
                        onChange={(_, value) =>
                            setSelected(value as SearchableSelectOption[])
                        }
                        TextFieldProps={{
                            label: 'Organizations',
                            placeholder:
                                selected.length === 0
                                    ? 'Select organizations'
                                    : '',
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button
                        variant="contained"
                        data-testid="assign-save"
                        disabled={stores.agentPrompts.isUpdating}
                        onClick={() => onSave(selected.map(o => o.value))}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        )
    })
